import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Shuffle, Sparkles, Wand2, ArrowRight, Loader2, CheckCircle2, RefreshCw,
  UserCircle2, Gamepad2, Blend, Palette, Boxes, Music, Images, Globe2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

const GENRES = [
  "Platformer", "Racing", "Shooter", "RPG", "Adventure", "Puzzle", "Fighting", "Brawler",
  "Tower defense", "Card battle", "Survival", "Roguelike", "Stealth", "Horror", "Kart racer",
  "Endless runner", "Open world", "Metroidvania", "Sports", "Rhythm", "Dungeon crawler",
  "City builder", "Farming sim", "Battle royale", "Sandbox",
];
const STYLES = [
  "Anime", "Cyberpunk", "Pixel art", "Retro 8-bit", "Cartoon", "Realistic 3D", "Fantasy",
  "Sci-fi", "Voxel", "Cel-shaded", "Watercolour", "Papercraft", "Neon", "Clay", "Comic book",
];
const MODES = [
  { id: "character", label: "Insert a Character", icon: UserCircle2, blurb: "Drop a hero into a whole new game — auto-restyled to fit." },
  { id: "blend", label: "Blend Two Genres", icon: Blend, blurb: "Fuse two genres into one fresh, playable mashup." },
  { id: "restyle", label: "Re-style a Game", icon: Palette, blurb: "Reimagine one of your games with a bold new look or genre." },
];

const MODEL_LABELS = { ast_player: "Hero", ast_enemy: "Enemy", ast_boss: "Boss" };
const ART_LABELS = { player: "Hero art", collectible: "Item art", enemy: "Enemy art", boss: "Boss art" };
const modelLabel = (aid) => {
  if (MODEL_LABELS[aid]) return MODEL_LABELS[aid];
  const m = String(aid).match(/npc_(\d+)/);
  return m ? `NPC ${parseInt(m[1], 10) + 1}` : "Character";
};

export default function MashupStudio() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState("character");
  const [chars, setChars] = useState([]);
  const [games, setGames] = useState([]);

  // form fields
  const [characterId, setCharacterId] = useState("");
  const [characterDesc, setCharacterDesc] = useState("");
  const [targetGameId, setTargetGameId] = useState("");
  const [targetDesc, setTargetDesc] = useState("");
  const [genreA, setGenreA] = useState("");
  const [genreB, setGenreB] = useState("");
  const [theme, setTheme] = useState("");
  const [baseGameId, setBaseGameId] = useState("");
  const [newStyle, setNewStyle] = useState("");
  const [newGenre, setNewGenre] = useState("");
  const [artStyle, setArtStyle] = useState("");
  const [dimension, setDimension] = useState("");
  const [worldScale, setWorldScale] = useState("");
  const [extraMeta, setExtraMeta] = useState({});

  // flow
  const [step, setStep] = useState("form"); // form | planning | review | building | generating
  const [result, setResult] = useState(null);
  const [modelStyle, setModelStyle] = useState("realistic");
  const [aaaMode, setAaaMode] = useState(true);
  const [soundTheme, setSoundTheme] = useState("auto");
  const [created, setCreated] = useState(null);
  const [genAssets, setGenAssets] = useState([]);
  const [genDone, setGenDone] = useState(false);

  useEffect(() => {
    api.get("/characters").then((r) => setChars(r.data || [])).catch(() => {});
    api.get("/games").then((r) => setGames((r.data || []).filter((g) => !g.shared))).catch(() => {});
  }, []);

  // Prefill from query params (Mashup of the Day / Remix on the Wall) — auto-run when ?go=1.
  useEffect(() => {
    const p = Object.fromEntries(sp.entries());
    if (!(p.mode || p.genre_a || p.character_desc || p.character_id || p.base_game_id)) return;
    if (p.mode) setMode(p.mode);
    if (p.character_id) setCharacterId(p.character_id);
    if (p.character_desc) setCharacterDesc(p.character_desc);
    if (p.target_game_id) setTargetGameId(p.target_game_id);
    if (p.target_desc) setTargetDesc(p.target_desc);
    if (p.genre_a) setGenreA(p.genre_a);
    if (p.genre_b) setGenreB(p.genre_b);
    if (p.theme) setTheme(p.theme);
    if (p.base_game_id) setBaseGameId(p.base_game_id);
    if (p.new_style) setNewStyle(p.new_style);
    if (p.new_genre) setNewGenre(p.new_genre);
    if (p.art_style) setArtStyle(p.art_style);
    if (p.dimension) setDimension(p.dimension);
    if (p.world_scale) setWorldScale(p.world_scale);
    const em = {};
    if (p.from) em.remix_of = { id: p.from };
    if (p.challenge) em.challenge = p.challenge;
    if (Object.keys(em).length) setExtraMeta(em);
    if (p.go === "1") {
      runPlan({
        mode: p.mode || "character",
        character_id: p.character_id || null, character_desc: p.character_desc || "",
        target_game_id: p.target_game_id || null, target_desc: p.target_desc || "",
        genre_a: p.genre_a || "", genre_b: p.genre_b || "", theme: p.theme || "",
        base_game_id: p.base_game_id || null, new_style: p.new_style || "", new_genre: p.new_genre || "",
        art_style: p.art_style || "", dimension: p.dimension || "", world_scale: p.world_scale || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPlan = async (body) => {
    setStep("planning");
    try {
      const { data } = await api.post("/mashup/plan", body);
      setResult(data);
      setStep("review");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStep("form");
    }
  };

  const buildBody = () => ({
    mode,
    character_id: characterId || null, character_desc: characterDesc.trim(),
    target_game_id: targetGameId || null, target_desc: targetDesc.trim(),
    genre_a: genreA.trim(), genre_b: genreB.trim(), theme: theme.trim(),
    base_game_id: baseGameId || null, new_style: newStyle.trim(), new_genre: newGenre.trim(),
    art_style: artStyle.trim(), dimension: dimension || "", world_scale: worldScale || "",
  });

  const plan = () => {
    if (mode === "character" && !characterId && !characterDesc.trim()) { toast.error("Pick a character or describe one to insert."); return; }
    if (mode === "blend" && (!genreA.trim() || !genreB.trim())) { toast.error("Pick two genres to blend."); return; }
    if (mode === "restyle" && !baseGameId) { toast.error("Pick one of your games to re-style."); return; }
    if (mode === "restyle" && !newStyle.trim() && !newGenre.trim()) { toast.error("Choose a new art style or a new genre."); return; }
    runPlan(buildBody());
  };

  const build = async () => {
    setStep("building");
    try {
      const { data } = await api.post("/games", {
        title: result.plan.title,
        idea: result.idea || result.plan.title,
        plan: result.plan, config: result.config, mashup: { ...(result.mashup || {}), ...extraMeta },
        art_style_3d: modelStyle, aaa_mode: aaaMode, sound_theme: soundTheme,
      });
      api.post("/quests/track", { event: "create" }).catch(() => {});
      if (data.model_generating || data.skybox_generating || data.art_generating || data.audio_generating) {
        setCreated(data); setGenAssets([]); setGenDone(false); setStep("generating");
      } else {
        toast.success("Mashup created! Opening the studio…");
        navigate(`/app/build/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStep("review");
    }
  };

  // ---- AAA build polling (mirrors Create Game) ----
  useEffect(() => {
    if (step !== "generating" || !created) return;
    let cancelled = false;
    const is3D = (result?.config?.dimension || "2d") === "3d";
    const poll = async () => {
      let rows = [], allDone = true;
      if (is3D) {
        try {
          const { data } = await api.get(`/bridge/models-status/${created.id}`);
          rows = (data.models || []).map((m) => ({ key: m.asset_id, label: modelLabel(m.asset_id), status: m.status, progress: m.progress || 0 }));
          if (!data.all_done) allDone = false;
        } catch {}
        try {
          const { data: g } = await api.get(`/games/${created.id}`);
          const sky = !!(g.config?.world?.skyboxUrl);
          rows.push({ key: "skybox", label: "Cinematic sky", status: sky ? "succeeded" : "processing", progress: sky ? 100 : 65 });
          if (!sky && created.skybox_generating) allDone = false;
        } catch {}
      } else {
        try {
          const { data } = await api.get(`/games/${created.id}/art-status`);
          rows = (data.targets || []).map((t) => ({ key: t.target, label: ART_LABELS[t.target] || (String(t.target).startsWith("npc:") ? `NPC ${parseInt(String(t.target).split(":")[1], 10) + 1} art` : t.target), status: t.status === "done" ? "succeeded" : (t.status === "failed" ? "failed" : "processing"), progress: t.status === "done" ? 100 : 55 }));
          if (!data.done) allDone = false;
        } catch {}
      }
      try {
        const { data: a } = await api.get(`/audio/pack-status/${created.id}`);
        if (a && (a.audio_generating || a.done || (a.events && a.events.length))) {
          const dc = (a.events || []).filter((e) => e.status === "done").length;
          const tot = (a.events || []).length || 6;
          rows.push({ key: "soundtrack", label: "Soundtrack · BGM + SFX", status: a.done ? "succeeded" : "processing", progress: a.done ? 100 : Math.max(20, Math.round((dc / tot) * 100)) });
          if (!a.done) allDone = false;
        }
      } catch {}
      if (!cancelled) { setGenAssets(rows); if (allDone && rows.length) setGenDone(true); }
      return allDone && rows.length;
    };
    poll();
    const t = setInterval(async () => { if (cancelled) return; const d = await poll(); if (d) clearInterval(t); }, 5000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, created]);

  // ---------- AAA BUILD PROGRESS ----------
  if (step === "generating" && created) {
    const total = genAssets.length || 1;
    const doneCount = genAssets.filter((a) => a.status === "succeeded").length;
    return (
      <div className="min-h-[80vh] grid place-items-center p-6" data-testid="mashup-build-panel">
        <div className="w-full max-w-lg animate-rise">
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4">
              <div className="absolute -inset-6 bg-fuchsia-400/20 blur-2xl rounded-full animate-pulse-glow" />
              <div className="w-24 h-24 relative rounded-3xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-2xl">
                <Shuffle className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="font-display text-2xl font-bold mb-1">{genDone ? "Your mashup is ready! 🎉" : "Fusing your mashup…"}</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">
              {genDone ? "Every asset is restyled and painted. Step into the studio." : "Restyling characters and painting a fresh world. This runs in the background."}
            </p>
          </div>
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 space-y-3 mb-5">
            {genAssets.length === 0 && <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Warming up the AI studio…</div>}
            {genAssets.map((a) => (
              <div key={a.key} className="flex items-center gap-3" data-testid={`mashup-gen-${a.key}`}>
                <div className="w-6 shrink-0">
                  {a.status === "succeeded" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    : a.status === "failed" ? <span className="text-amber-500 text-sm">⚠️</span>
                    : <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--primary))]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate">{a.label}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{a.status === "succeeded" ? "Ready" : a.status === "failed" ? "Skipped" : "Building…"}</span>
                  </div>
                  <div className="h-1.5 mt-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-cyan-500 transition-all duration-700" style={{ width: `${a.status === "succeeded" ? 100 : a.progress}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button onClick={() => navigate(`/app/build/${created.id}`)} data-testid="mashup-enter-studio-btn" size="lg"
              className={`rounded-full px-8 py-6 w-full ${genDone ? "bg-gradient-to-r from-fuchsia-500 to-cyan-500 brand-glow" : "bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"} hover:opacity-90`}>
              {genDone ? <><Sparkles className="w-5 h-5" /> Enter your studio</> : <>Open studio now <ArrowRight className="w-5 h-5" /></>}
            </Button>
            {!genDone && <span className="text-xs text-[hsl(var(--muted-foreground))]">{doneCount}/{total} assets ready — the rest keep building in the studio</span>}
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLANNING / BUILDING ----------
  if (step === "planning" || step === "building") {
    return (
      <div className="min-h-[80vh] grid place-items-center p-6">
        <div className="text-center animate-rise">
          <div className="relative inline-block mb-6">
            <div className="absolute -inset-6 bg-fuchsia-500/20 blur-2xl rounded-full animate-pulse-glow" />
            <div className="w-28 h-28 relative rounded-3xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-2xl animate-float">
              <Shuffle className="w-14 h-14 text-white" />
            </div>
          </div>
          <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-[hsl(var(--primary))]" />
          <h2 className="font-display text-2xl font-bold mb-2">{step === "planning" ? "Fusing your mashup idea…" : "Building your mashup…"}</h2>
          <p className="text-[hsl(var(--muted-foreground))]">This takes just a few seconds. ✨</p>
        </div>
      </div>
    );
  }

  // ---------- REVIEW ----------
  if (step === "review" && result) {
    const p = result.plan, c = result.config;
    const is3D = (c.dimension || "2d") === "3d";
    return (
      <div className="max-w-4xl mx-auto p-5 lg:p-8 animate-rise">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Your mashup plan</h1>
        </div>

        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 mb-5">
          <div className="flex items-start gap-4">
            <div className="text-5xl">{c.player?.emoji || "🎮"}</div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-bold">{p.title}</h2>
              <p className="text-[hsl(var(--muted-foreground))] mt-1">{p.description}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge>{p.genre}</Badge>
                <Badge>{is3D ? "🧊 3D" : "🕹️ 2D"}</Badge>
                <Badge>Ages {p.target_age}</Badge>
                <Badge>{p.difficulty}</Badge>
                <Badge>Template: {c.template}</Badge>
                {p.safety_rating && <Badge>🛡️ {p.safety_rating}</Badge>}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden mb-6">
          {[
            ["Gameplay loop", p.gameplay_loop], ["Objective", p.objective], ["Controls", p.controls],
            ["Enemies / obstacles", p.enemies], ["Collectibles", p.collectibles],
            ["Win condition", p.win_condition], ["Visual style", p.visual_style],
          ].filter(([, v]) => v).map(([k, v], i) => (
            <div key={k} className={`grid grid-cols-3 gap-3 px-5 py-3 text-sm ${i % 2 ? "bg-[hsl(var(--secondary))]/40" : ""}`}>
              <div className="font-semibold text-[hsl(var(--muted-foreground))]">{k}</div>
              <div className="col-span-2">{v}</div>
            </div>
          ))}
        </div>

        {is3D && (
          <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-transparent p-4 mb-6" data-testid="mashup-model-style">
            <div className="flex items-center gap-2 mb-1 text-sm font-bold"><Boxes className="w-4 h-4 text-[hsl(var(--primary))]" /> 3D character style</div>
            <div className="grid grid-cols-2 gap-2 max-w-md mt-2">
              {[["realistic", "Realistic", "Detailed, textured & lifelike"], ["sculpture", "Stylized", "Clean sculpted, clay-like look"]].map(([v, label, desc]) => (
                <button key={v} type="button" onClick={() => setModelStyle(v)} data-testid={`mashup-model-${v}`}
                  className={`text-left rounded-xl border p-3 transition-all ${modelStyle === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
                  <div className="font-semibold text-sm flex items-center gap-1.5">{modelStyle === v && <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}{label}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-400/10 to-transparent p-4 mb-6" data-testid="mashup-aaa-toggle">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="w-4 h-4 text-amber-500" /> AAA mode</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {is3D ? "Auto-sculpt real 3D models + a cinematic AI skybox." : "Auto-draw real AI sprite art for your whole cast."} (Uses a few AI credits.)
              </p>
            </div>
            <button type="button" role="switch" aria-checked={aaaMode} onClick={() => setAaaMode((v) => !v)} data-testid="mashup-aaa-switch"
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${aaaMode ? "bg-amber-500" : "bg-[hsl(var(--muted))]"}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${aaaMode ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-400/10 to-transparent p-4 mb-6" data-testid="mashup-sound-toggle">
          <div className="flex items-center gap-2 text-sm font-bold"><Music className="w-4 h-4 text-fuchsia-500" /> Soundtrack style</div>
          <div className="flex flex-wrap gap-2 mt-3">
            {[["auto", "✨ Auto-match"], ["retro", "🕹️ Retro"], ["fantasy", "🏰 Fantasy"], ["scifi", "🚀 Sci-Fi"], ["cute", "🍬 Cute"], ["action", "💥 Action"]].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setSoundTheme(v)} data-testid={`mashup-sound-${v}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${soundTheme === v ? "border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={build} data-testid="mashup-build-btn" size="lg"
            className="rounded-full px-8 py-6 bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 brand-glow text-white">
            <Wand2 className="w-5 h-5" /> Build my mashup!
          </Button>
          <Button onClick={plan} variant="outline" size="lg" className="rounded-full px-6 py-6" data-testid="mashup-regenerate-btn">
            <RefreshCw className="w-4 h-4" /> Try a different fusion
          </Button>
          <Button onClick={() => setStep("form")} variant="ghost" size="lg" className="rounded-full px-6 py-6" data-testid="mashup-edit-btn">Edit mashup</Button>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  return (
    <div className="max-w-4xl mx-auto p-5 lg:p-8" data-testid="mashup-studio">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-lg">
            <Shuffle className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-3xl font-extrabold">Mashup Studio</h1>
        </div>
        <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => navigate("/app/mashup-gallery")} data-testid="mashup-gallery-link">
          <Images className="w-4 h-4" /> Mashup Wall
        </Button>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Remix what you already have — drop a character into a new world, blend two genres, or reinvent a game's whole look. The AI restyles everything to fit and builds a full, playable game.</p>

      {/* Mode selector */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="mashup-modes">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`mashup-mode-${m.id}`}
              className={`text-left rounded-2xl border-2 p-4 transition-all ${active ? "border-fuchsia-500 bg-fuchsia-500/10 shadow-lg shadow-fuchsia-500/10" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>
              <Icon className={`w-6 h-6 mb-2 ${active ? "text-fuchsia-500" : "text-[hsl(var(--muted-foreground))]"}`} />
              <div className="font-bold text-sm">{m.label}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{m.blurb}</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 lg:p-8 brand-glow space-y-6">
        {mode === "character" && (
          <>
            <div data-testid="mashup-char-picker">
              <Label className="text-sm font-bold flex items-center gap-1.5 mb-2"><UserCircle2 className="w-4 h-4 text-fuchsia-500" /> Pick a character to insert</Label>
              {chars.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">No characters yet — make one in Character Studio, or just describe one below.</p>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {chars.map((c) => (
                    <button key={c.id} type="button" onClick={() => { setCharacterId(characterId === c.id ? "" : c.id); if (characterId !== c.id) setCharacterDesc(""); }} data-testid={`mashup-char-${c.id}`}
                      className={`shrink-0 w-24 rounded-xl border-2 p-2 text-center transition-all ${characterId === c.id ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>
                      {c.image ? <img src={asset(c.image)} alt={c.name} className="w-12 h-12 mx-auto rounded-lg object-cover mb-1" loading="lazy" /> : <div className="text-3xl mb-1">{c.emoji || "🎭"}</div>}
                      <div className="text-[11px] font-semibold truncate">{c.name}</div>
                    </button>
                  ))}
                </div>
              )}
              <Textarea value={characterDesc} onChange={(e) => { setCharacterDesc(e.target.value); if (e.target.value.trim()) setCharacterId(""); }} data-testid="mashup-char-desc"
                placeholder="…or describe a character to insert (e.g. a brave fox knight in silver armour)" className="mt-3 min-h-[70px] resize-none text-sm" />
            </div>

            <div data-testid="mashup-target-picker">
              <Label className="text-sm font-bold flex items-center gap-1.5 mb-2"><Gamepad2 className="w-4 h-4 text-cyan-500" /> Drop them into…</Label>
              {games.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {games.map((g) => (
                    <button key={g.id} type="button" onClick={() => { setTargetGameId(targetGameId === g.id ? "" : g.id); if (targetGameId !== g.id) setTargetDesc(""); }} data-testid={`mashup-target-${g.id}`}
                      className={`shrink-0 min-w-[130px] max-w-[160px] rounded-xl border-2 p-3 text-left transition-all ${targetGameId === g.id ? "border-cyan-500 bg-cyan-500/10" : "border-[hsl(var(--border))] hover:border-cyan-500/50"}`}>
                      <div className="text-2xl mb-1">{g.config?.player?.emoji || "🎮"}</div>
                      <div className="text-[12px] font-semibold truncate">{g.title}</div>
                      <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{(g.config?.dimension || "2d") === "3d" ? "3D" : "2D"} · {g.template}</div>
                    </button>
                  ))}
                </div>
              )}
              <Input value={targetDesc} onChange={(e) => { setTargetDesc(e.target.value); if (e.target.value.trim()) setTargetGameId(""); }} data-testid="mashup-target-desc"
                placeholder="…or describe a target game/style (e.g. a neon cyberpunk racing game)" className="mt-3" />
            </div>
          </>
        )}

        {mode === "blend" && (
          <>
            <GenreField label="Genre A" testid="mashup-genre-a" value={genreA} onChange={setGenreA} />
            <GenreField label="Genre B" testid="mashup-genre-b" value={genreB} onChange={setGenreB} />
            <div>
              <Label className="text-sm font-bold mb-2 block">Theme (optional)</Label>
              <Input value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="mashup-theme" placeholder="e.g. undersea kingdom, haunted circus, space western" />
            </div>
          </>
        )}

        {mode === "restyle" && (
          <>
            <div data-testid="mashup-base-picker">
              <Label className="text-sm font-bold flex items-center gap-1.5 mb-2"><Gamepad2 className="w-4 h-4 text-fuchsia-500" /> Pick one of your games</Label>
              {games.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">No games yet — create one first, then come back to re-style it.</p>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {games.map((g) => (
                    <button key={g.id} type="button" onClick={() => setBaseGameId(baseGameId === g.id ? "" : g.id)} data-testid={`mashup-base-${g.id}`}
                      className={`shrink-0 min-w-[130px] max-w-[160px] rounded-xl border-2 p-3 text-left transition-all ${baseGameId === g.id ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>
                      <div className="text-2xl mb-1">{g.config?.player?.emoji || "🎮"}</div>
                      <div className="text-[12px] font-semibold truncate">{g.title}</div>
                      <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{(g.config?.dimension || "2d") === "3d" ? "3D" : "2D"} · {g.template}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm font-bold mb-2 block flex items-center gap-1.5"><Palette className="w-4 h-4 text-cyan-500" /> New art style</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {STYLES.map((s) => (
                  <button key={s} type="button" onClick={() => setNewStyle(newStyle === s ? "" : s)} data-testid={`mashup-newstyle-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold transition-all ${newStyle === s ? "border-fuchsia-500 bg-fuchsia-500/15" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>{s}</button>
                ))}
              </div>
              <Input value={newStyle} onChange={(e) => setNewStyle(e.target.value)} data-testid="mashup-newstyle-input" placeholder="…or type any style" />
            </div>
            <GenreField label="New genre (optional)" testid="mashup-newgenre" value={newGenre} onChange={setNewGenre} />
          </>
        )}

        {/* Common overrides */}
        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[hsl(var(--border))]">
          <div>
            <Label className="text-sm mb-1.5 block">Art style override (optional)</Label>
            <Input value={artStyle} onChange={(e) => setArtStyle(e.target.value)} data-testid="mashup-artstyle" placeholder="pixel art, anime, neon…" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Dimension</Label>
            <div className="flex gap-2" data-testid="mashup-dimension">
              {[["", "Auto ✨"], ["2d", "2D 🕹️"], ["3d", "3D 🧊"]].map(([v, l]) => (
                <button key={v || "auto"} type="button" onClick={() => setDimension(v)} data-testid={`mashup-dim-${v || "auto"}`}
                  className={`flex-1 py-2.5 rounded-xl font-bold border-2 text-sm transition-all ${dimension === v ? "border-fuchsia-500 bg-fuchsia-500/15" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        {dimension !== "2d" && (
          <div data-testid="mashup-worldscale-row">
            <Label className="text-sm mb-1.5 block flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-cyan-500" /> World scale <span className="font-normal text-[hsl(var(--muted-foreground))]">— for 3D open worlds</span></Label>
            <div className="flex gap-2" data-testid="mashup-worldscale">
              {[["", "Compact"], ["large", "Large 🌄"], ["huge", "Huge 🌍"]].map(([v, l]) => (
                <button key={v || "compact"} type="button" onClick={() => setWorldScale(v)} data-testid={`mashup-scale-${v || "compact"}`}
                  className={`flex-1 py-2.5 rounded-xl font-bold border-2 text-sm transition-all ${worldScale === v ? "border-cyan-500 bg-cyan-500/15" : "border-[hsl(var(--border))] hover:border-cyan-500/50"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}

        <Button onClick={plan} data-testid="mashup-plan-btn" size="lg"
          className="w-full rounded-full py-6 text-base bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 brand-glow text-white">
          <Sparkles className="w-5 h-5" /> Fuse my mashup <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}

function GenreField({ label, testid, value, onChange }) {
  return (
    <div>
      <Label className="text-sm font-bold mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2 max-h-28 overflow-y-auto pr-1">
        {GENRES.map((g) => (
          <button key={g} type="button" onClick={() => onChange(value === g ? "" : g)} data-testid={`${testid}-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold transition-all ${value === g ? "border-fuchsia-500 bg-fuchsia-500/15" : "border-[hsl(var(--border))] hover:border-fuchsia-500/50"}`}>{g}</button>
        ))}
      </div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} placeholder="…or type your own" />
    </div>
  );
}

const Badge = ({ children }) => (
  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--secondary))]">{children}</span>
);
